import AddIcon from "@mui/icons-material/Add";
import ClearIcon from "@mui/icons-material/Clear";
import SearchIcon from "@mui/icons-material/Search";
import {
  Box,
  Container,
  Fab,
  IconButton,
  InputAdornment,
  TextField,
} from "@mui/material";
import Alert from "@mui/material/Alert";
import LazyTable, {
  DEFAULT_ROWS_PER_PAGE,
  TableCellType,
  TableSortType,
} from "components/LazyTable";
import OpenLinkInNewTabContextMenuItem from "components/OpenInNewTabContextMenuItem";
import useGlobalContext from "hooks/globalData";
import useDebounce from "hooks/useDebounce";
import useListTitle from "hooks/useListTitle";
import { useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { useQuery, useQueryClient } from "react-query";
import { Link, useNavigate } from "react-router-dom";
import {
  AdminListRequestOfBaseFilter,
  IListResponseOfCertificate,
  ListResponseOfCertificate,
} from "services/KnowfullyAdminClient";
import contextStore from "stores/contextStore";
import persistanceStore from "stores/persistanceStore";

interface IListResponseOfCertificateWithId extends IListResponseOfCertificate {
  id: string;
}

const CertificateList = () => {
  const queryClient = useQueryClient();
  const { formatMessage: t } = useIntl();
  const {
    certificateService,
    certificateCreateUrl,
    certificateDetailsUrl,
  } = useGlobalContext();

  const { organizationId } = contextStore.useState();

  const navigate = useNavigate();
  const [page, setPage] = useState(0);
  const [order, setOrder] = useState<TableSortType>("asc");
  const [orderBy, setOrderBy] = useState("name");
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_ROWS_PER_PAGE);
  const searchTerm = persistanceStore.useState((s) => s.certificateSearchTerm);
  const trimmedSearchTerm = searchTerm.trim();
  const debouncedSearchTerm = useDebounce(trimmedSearchTerm, 500);

  useListTitle("certificatesLabel");

  const {
    isError,
    data,
    error,
    isFetching,
  } = useQuery<ListResponseOfCertificate>(
    [
      "certificates",
      organizationId,
      page,
      rowsPerPage,
      orderBy,
      order,
      debouncedSearchTerm,
    ],
    () =>
      certificateService.getCertificates(
        AdminListRequestOfBaseFilter.fromJS({
          organizationId,
          pageNumber: page,
          pageSize: rowsPerPage,
          sortField: orderBy,
          sortDescending: order === "desc",
          parameters: {
            searchText: debouncedSearchTerm,
          },
        })
      ),
    { keepPreviousData: true }
  );

  useEffect(() => {
    setPage(0);
  }, [debouncedSearchTerm, organizationId]);

  if (isError) {
    return (
      <Alert severity="error">
        <pre>{JSON.stringify(error, null, 2)}</pre>
      </Alert>
    );
  }

  const cells: Array<TableCellType> = [
    {
      accessor: "name",
      align: "left",
      type: "text",
      label: t({ id: "certificateNameLabel" }),
    },
    {
      accessor: "description",
      align: "left",
      type: "text",
      label: t({ id: "certificateDescriptionLabel" }),
    },
    {
      accessor: "organizationName",
      customSortAccessor: "organization.Name",
      align: "left",
      type: "text",
      label: t({ id: "organization" }),
    },
  ];

  const certificates = data?.data as IListResponseOfCertificateWithId[];

  const onClick = (id: string) => {
    queryClient.invalidateQueries(["certificateSkus", id]);
    queryClient.invalidateQueries(["certificateProducts", id]);
    navigate(certificateDetailsUrl(id));
  };

  return (
    <Container>
      <Box mb={3} textAlign="center">
        <Link to={certificateCreateUrl()} style={{ textDecoration: "none" }}>
          <Fab aria-label="add" color="primary" data-cy="addButton">
            <AddIcon />
          </Fab>
        </Link>
      </Box>

      <Box mb={3}>
        <TextField
          fullWidth
          variant="outlined"
          value={searchTerm ?? ""}
          placeholder={t({ id: "searchPlaceholder" })}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  onClick={() =>
                    persistanceStore.update((s) => {
                      s.certificateSearchTerm = "";
                    })
                  }
                >
                  <ClearIcon />
                </IconButton>
              </InputAdornment>
            ),
          }}
          onChange={(e) => {
            persistanceStore.update((s) => {
              s.certificateSearchTerm = e.target.value;
            });
          }}
          data-cy="searchText"
        />
      </Box>

      <LazyTable
        {...{
          rowCount: data?.totalRecords ?? 0,
          rows: certificates ?? [],
          loading: isFetching,
          dense: true,
          title: t({ id: "certificatesLabel" }),
          cells,
          page,
          setPage,
          order,
          setOrder,
          orderBy,
          setOrderBy,
          rowsPerPage,
          setRowsPerPage,
          onClick,
          contextMenuItems: [
            OpenLinkInNewTabContextMenuItem((id) => certificateDetailsUrl(id)),
          ],
        }}
      />
    </Container>
  );
};

export default CertificateList;
